import cx from 'classnames'
import { useContext, useEffect, useRef, useState } from 'react'
import { useResizeObserver } from 'usehooks-ts'

import {
  type SanityHeaderSettings,
  type SanitySiteFragment,
} from '@data/sanity/queries/types/site'
import { CartContext } from '@lib/cart/context'
import { SearchContext } from '@lib/search-context'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'

import MegaNavigation from '@blocks/navigation/mega-navigation'
import Menu from '@blocks/navigation/menu'
import SearchDrawer from '@blocks/navigation/search-drawer'
import SearchToggle from '@blocks/navigation/search-toggle'
import CartToggle from '@components/cart-toggle'
import PromoBar from '../promo-bar'
import Logo, { Logos } from './logo'
import MobileMenu from './mobile-menu'
import MobileMenuToggle from './mobile-menu-toggle'

export interface HeaderSizeValues {
  height: number
}

type HeaderProps = Pick<SanitySiteFragment, 'promoSettings'> &
  SanityHeaderSettings & {
    isTransparent: boolean
    isInView: boolean
    logos?: Logos
    onResize?: (newValues: HeaderSizeValues) => void
  }

const Header = ({
  promoSettings,
  menuDesktopLeft,
  menuDesktopRight,
  menuMobilePrimary,
  menuMobileSecondary,
  isTransparent,
  isInView,
  logos,
  onResize,
}: HeaderProps) => {
  const strings = useContext(StringsContext)
  const { megaNavigation, mobileMenu, toggleMegaNavigation } =
    useContext(SiteContext)
  const { searchDrawer } = useContext(SearchContext)
  const { toggleCart } = useContext(CartContext)

  const [isLoaded, setIsLoaded] = useState(false)
  const [isObserverVisible, setIsObserverVisible] = useState(isTransparent)

  const headerHeightRef = useRef<number>()
  const headerRef = useRef<HTMLDivElement | null>(null)
  const headerRectangle = useResizeObserver({
    ref: headerRef,
  })

  const headerLinkClasses =
    'appearance-none no-underline cursor-pointer font-inherit transition-opacity hover:opacity-60 text-base md:text-lg'

  useEffect(() => {
    setIsObserverVisible(
      isLoaded
        ? isInView && !searchDrawer.isOpen && !mobileMenu.isOpen
        : isTransparent
    )
  }, [
    isInView,
    isLoaded,
    isTransparent,
    searchDrawer.isOpen,
    mobileMenu.isOpen,
  ])

  // Wait for intersection observer to load, then show header
  useEffect(() => {
    if (!isLoaded) {
      setTimeout(() => {
        setIsLoaded(true)
      }, 200)
    }
  }, [isLoaded])

  useEffect(() => {
    if (!isLoaded) {
      return
    }

    if (
      headerRectangle.height &&
      headerHeightRef.current !== headerRectangle.height
    ) {
      headerHeightRef.current = headerRectangle.height

      if (onResize) {
        onResize({
          height: headerRectangle.height,
        })
      }
    }
  }, [headerRectangle, isLoaded, onResize])

  return (
    <>
      <a
        href="#content"
        className="block fixed top-0 left-1/2 transform -translate-x-1/2 -translate-y-full z-90 px-2 py-1 bg-pageBG text-pageText text-xs font-semibold uppercase focus:translate-y-0 focus:outline-none"
      >
        {strings.skipToContent}
      </a>

      {!!promoSettings?.enabled && (
        <PromoBar
          enabled={promoSettings.enabled}
          display={promoSettings.display}
          messages={promoSettings.messages}
          link={promoSettings.link}
        />
      )}

      <MobileMenu
        menuMobilePrimary={menuMobilePrimary}
        menuMobileSecondary={menuMobileSecondary}
      />

      <header
        className={cx('sticky top-0 inset-x-0 z-50 text-header-text ', {
          'h-0': isTransparent,
        })}
      >
        <div ref={headerRef} className="relative">
          <div
            className={cx('transition-colors duration-300', {
              'bg-transparent': isTransparent && isObserverVisible,
              'bg-header-bg': !isTransparent,
              'bg-header-bg bg-opacity-95': !isObserverVisible,
              'text-white delay-75':
                isTransparent && !megaNavigation?.isOpen && isObserverVisible,
            })}
          >
            <nav
              className={cx('relative z-60', {
                'transition-all': !isTransparent,
                'border-b border-white': isTransparent && isObserverVisible,
                'border-b border-pageText':
                  (!isTransparent || megaNavigation?.isOpen) &&
                  isObserverVisible,
              })}
            >
              <div className="px-[var(--container-padding)] xs:px-[calc(var(--container-padding)+var(--page-padding))] relative flex items-center justify-between py-4 lg:py-5 gap-x-5 lg:gap-x-16">
                {/* Left Navigation/Menu */}
                <div>
                  {!!menuDesktopLeft?.items && (
                    <Menu
                      id={menuDesktopLeft.slug.current}
                      menuType="header-desktop"
                      items={menuDesktopLeft.items}
                      onClick={() => toggleMegaNavigation(false)}
                      useMegaNav
                      className="hidden lg:flex"
                    />
                  )}
                </div>

                {/* Logo */}
                <div
                  className={cx(
                    'absolute inset-0',
                    'ml-[var(--container-padding)] xs:ml-[calc(var(--container-padding)+var(--page-padding))]',
                    'flex justify-start items-center max-w-[45%] lg:mx-auto'
                  )}
                  style={{
                    height: 'auto',
                    width: 'auto',
                  }}
                >
                  {logos && (
                    <Logo
                      logos={logos}
                      isInvertedLogo={isTransparent && isObserverVisible}
                    />
                  )}
                </div>

                {/* Right Navigation/Actions */}
                <div className="flex items-center gap-x-3 sm:gap-x-4">
                  <SearchToggle>
                    <span className={headerLinkClasses}>
                      {strings.searchLabel}
                    </span>
                  </SearchToggle>

                  {!!menuDesktopRight?.items && (
                    <Menu
                      id={menuDesktopRight.slug.current}
                      menuType="header-desktop"
                      items={menuDesktopRight.items}
                      onClick={() => toggleMegaNavigation(false)}
                      useMegaNav
                      className="hidden lg:flex"
                    />
                  )}

                  <CartToggle
                    onClick={() => toggleCart(true)}
                    className="mr-2 lg:mr-1"
                    showCartCount={true}
                  >
                    <span className={headerLinkClasses}>
                      {strings.cartLabel}
                    </span>
                  </CartToggle>

                  <MobileMenuToggle className="lg:hidden" />
                </div>
              </div>
            </nav>
          </div>
        </div>

        <MegaNavigation
          items={[
            ...(menuDesktopLeft?.items ?? []),
            ...(menuDesktopRight?.items ?? []),
          ]}
          headerHeight={headerRectangle?.height ?? 0}
        />
        <SearchDrawer headerHeight={headerRectangle?.height ?? 0} />
      </header>
    </>
  )
}

export default Header
